/**
 * Companions: the read-only preview characters loaded beside the open project.
 *
 * The store holds their *names* only — the live roots and rigs are session objects. One action is
 * enough because the whole list is replaced at once by `previewController`, which is the single
 * writer: a companion is never edited, so there is nothing to patch in place.
 *
 * Not undoable on purpose. Loading a second character into the viewport is a view decision, like
 * the Skeleton/Mesh display switches, not an edit to the user's data — putting it on the undo
 * stack would make Ctrl-Z silently unload a model instead of reverting an animation change.
 */

import type { CompanionMeta } from '../types';
import type { ActionContext } from './context';

export interface CompanionActions {
  /** Replace the companion list wholesale (the only mutation companions have). */
  setCompanions(list: readonly CompanionMeta[]): void;
}

export function createCompanionActions(ctx: ActionContext): CompanionActions {
  return {
    setCompanions(list): void {
      const current = ctx.getState().companions;
      const next = list.map((entry) => ({
        projectId: entry.projectId,
        name: entry.name,
        kind: entry.kind,
      }));
      // Identity is what panels subscribe to, so an unchanged list must not produce a new array.
      if (
        current.length === next.length &&
        current.every(
          (entry, i) =>
            entry.projectId === next[i].projectId &&
            entry.name === next[i].name &&
            entry.kind === next[i].kind,
        )
      ) {
        return;
      }
      ctx.set({ companions: next });
    },
  };
}
